import React from "react";
import { KpiCard } from "@/components/KpiCard";
import { TrendLineChart } from "@/components/Charts";
import { fmtCurrency, fmtNumber, fmtPct, fmtDecimal } from "@/lib/format";
import { TrendingUp, ArrowUpRight } from "lucide-react";
import { SummaryBanner, SectionBox, EmptyBlock } from "@/components/DashboardBits";

export default function ExecutiveSnapshot({ analysis, forecast }) {
  const k = analysis.kpis || {};
  const opps = analysis.top_revenue_opportunities || [];

  const summary = `Across ${fmtNumber(analysis.rows)} encounters, the hospital billed ${fmtCurrency(
    k.total_revenue
  )}${
    k.net_collected_revenue ? ` and collected ${fmtCurrency(k.net_collected_revenue)}` : ""
  }${k.denial_rate_pct !== null ? ` at a ${fmtPct(k.denial_rate_pct)} denial rate` : ""}${
    k.avg_margin_pct !== null ? `. Blended margin is ${fmtPct(k.avg_margin_pct)}` : ""
  }.`;

  return (
    <div className="space-y-8">
      <SummaryBanner
        title="Financial Snapshot"
        text={summary}
        testId="exec-summary-banner"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="exec-kpis">
        <KpiCard
          testId="kpi-total-revenue"
          label="Total Revenue (Billed)"
          value={fmtCurrency(k.total_revenue)}
          sub={k.total_encounters ? `${fmtNumber(k.total_encounters)} encounters` : null}
        />
        <KpiCard
          testId="kpi-net-collected"
          label="Net Collected Revenue"
          value={fmtCurrency(k.net_collected_revenue)}
          sub={
            k.claims_leakage_pct !== null
              ? `Leakage ${fmtPct(k.claims_leakage_pct)}`
              : null
          }
        />
        <KpiCard
          testId="kpi-total-claims"
          label="Total Claims"
          value={fmtNumber(k.total_claims)}
          sub={k.total_denials !== null ? `${fmtNumber(k.total_denials)} denied` : null}
        />
        <KpiCard
          testId="kpi-denial-rate"
          label="Denial Rate"
          value={k.denial_rate_pct !== null ? fmtPct(k.denial_rate_pct) : "—"}
          tone={k.denial_rate_pct > 10 ? "negative" : k.denial_rate_pct > 5 ? "default" : "positive"}
          sub="Payer-filed denials"
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <KpiCard
          testId="kpi-avg-margin"
          label="Avg Margin"
          value={k.avg_margin_pct !== null ? fmtPct(k.avg_margin_pct) : "—"}
          tone={k.avg_margin_pct < 10 ? "negative" : "default"}
          sub={k.total_margin !== null ? fmtCurrency(k.total_margin) : null}
        />
        <KpiCard
          testId="kpi-bed-occupancy"
          label="Bed Occupancy"
          value={k.avg_bed_occupancy_pct !== null ? fmtPct(k.avg_bed_occupancy_pct, 0) : "—"}
          sub="Average utilization"
        />
        <KpiCard
          testId="kpi-avg-los"
          label="Avg Length of Stay"
          value={k.avg_length_of_stay !== null ? `${fmtDecimal(k.avg_length_of_stay)} d` : "—"}
          sub="Days per encounter"
        />
        <KpiCard
          testId="kpi-rev-per-patient"
          label="Revenue / Patient"
          value={fmtCurrency(k.avg_revenue_per_patient)}
          sub="Blended across all cases"
        />
      </div>

      <SectionBox
        title="Top 3 Revenue Opportunities"
        eyebrow="/ Act this quarter"
        testId="top-opportunities"
        description="Quick-win revenue levers ranked by projected impact. All low-care-risk."
      >
        {opps.length ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {opps.map((o, i) => (
              <article
                key={o.title}
                className="border border-gray-200 p-6 bg-white hover:border-[#5E3BFF] transition-colors"
                data-testid={`opportunity-${i}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#5E3BFF]">
                    / {o.tag}
                  </span>
                  <span className="font-display text-2xl font-black text-gray-300">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h4 className="font-display font-bold text-lg tracking-tight text-gray-950 leading-snug">
                  {o.title}
                </h4>
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-1">
                    Projected Impact
                  </div>
                  <div className="font-display text-3xl font-black tracking-tighter text-[#16A34A] flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" strokeWidth={2} />
                    {fmtCurrency(o.estimate)}
                  </div>
                  <div className="text-xs text-gray-500 mt-2">{o.basis}</div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <EmptyBlock text="Not enough financial fields to calculate revenue opportunities." />
        )}
      </SectionBox>

      <SectionBox title="Monthly Revenue Trend" eyebrow="/ Performance" testId="exec-revenue-trend">
        {forecast?.history?.length ? (
          <TrendLineChart history={forecast.history} forecast={forecast.forecast} metric="revenue" />
        ) : (
          <EmptyBlock text="No date column detected to build a trend." />
        )}
      </SectionBox>
    </div>
  );
}
